import * as Popover from "@radix-ui/react-popover";
import * as React from "react";

import { faChevronDown } from "@performyard/fontawesome/pro-regular-svg-icons";

import { MultipleSelectDropdown } from "../components/MultipleSelectDropdown";

import { Option } from "../../../../types/Option";

import { useSelectBase } from "../../../../hooks";

import { cn } from "../../../../lib/utils";

import { Icon } from "../../../atoms/Icon";
import { type DragHandlers, SortableOrderedList } from "../../SortableOrderedList";

const inputContainerBaseClasses =
	"group relative box-border flex h-full min-h-8 items-center rounded-1 border border-solid border-neutral-3 bg-neutral-0 data-[errored=true]:border-errored data-[disabled=true]:cursor-not-allowed data-[disabled=true]:opacity-disabled data-[disabled=false]:cursor-default data-[disabled=false]:hover:border-neutral-5 data-[disabled=false]:active:border-neutral-5 data-[focused=true]:border-neutral-5";

type MultipleSelectOrderedInputProps<T> = {
	/**
	 * Array of options to display in the dropdown. If not provided and `fetchOptions` is used, options will be fetched dynamically.
	 */
	options?: Option<T>[];
	/**
	 * Async function to fetch options based on search term. When provided, enables server-side search and filtering.
	 * @param searchTerm - The current search term entered by the user
	 * @returns Promise that resolves to an array of options matching the search term
	 */
	fetchOptions?: (searchTerm: string) => Promise<Option<T>[]>;
	/**
	 * Currently selected values array. Order of values determines display order. Controlled component - parent manages state.
	 */
	value: T[];
	/**
	 * Callback function called when selection changes or items are reordered.
	 * @param value - The new array of selected values in their current order
	 */
	onChange: (value: T[]) => void;
	/**
	 * Optional callback function called when search value changes (combobox variant only).
	 * @param searchValue - The current search term
	 */
	onSearchChange?: (searchValue: string) => void;
	/**
	 * Whether the input is in an error state (displays red border).
	 * @default false
	 */
	errored?: boolean;
	/**
	 * Whether the input is disabled (not interactive, grayed out, drag disabled).
	 * @default false
	 */
	disabled?: boolean;
	/**
	 * Selection variant mode.
	 * - "select": Predefined list with keyboard navigation, no typeahead (default)
	 * - "combobox": Searchable list with filtering and virtualization
	 * @default "select"
	 */
	selectionVariant?: "select" | "combobox";
	/**
	 * Whether to close the dropdown after each selection in multiple mode.
	 * @default false
	 */
	closeOnSelect?: boolean;
	/**
	 * Placeholder text shown when no options are selected.
	 * @default "Select options..."
	 */
	placeholder?: string;
	/**
	 * Unique identifier for the input element. If not provided, will use context ID from InputWrapper or generate one.
	 */
	id?: string;
	/**
	 * Test identifier for the input element (used for testing).
	 */
	"data-testid"?: string;
	/**
	 * Custom render function for displaying ordered items in the list.
	 * @param value - The selected value to render
	 * @param index - The 1-based index of this item (display number)
	 * @param onRemove - Callback function to remove this item
	 * @param dragHandlers - Drag and drop handlers with listeners and attributes for the drag handle
	 * @param isDragging - Whether this item is currently being dragged
	 * @returns React node to render for the ordered item
	 */
	renderOrderedItem?: (
		value: T,
		index: number,
		onRemove: () => void,
		dragHandlers: DragHandlers,
		isDragging: boolean
	) => React.ReactNode;
	/**
	 * Custom render function for displaying options in the dropdown.
	 * @param option - The option to render
	 * @param isSelected - Whether this option is currently selected
	 * @returns React node to render for the option
	 */
	renderOption?: (option: Option<T>, isSelected: boolean) => React.ReactNode;
	/**
	 * Optional CSS classes to apply to the component container (use with caution).
	 */
	dangerouslySetClassName?: string;
	/**
	 * Configuration for virtualized rendering (combobox variant only).
	 */
	virtualizationConfig?: {
		/**
		 * Height of each virtualized item in pixels.
		 * @default 32
		 */
		itemHeight?: number;
		/**
		 * Maximum height of the dropdown container in pixels.
		 * @default 240
		 */
		maxHeight?: number;
	};
	/**
	 * Preferred position for the dropdown to appear relative to the trigger.
	 * Radix UI will automatically flip to the opposite side if there's not enough space.
	 * @default "bottom"
	 */
	position?: "top" | "right" | "bottom" | "left";
};

const MultipleSelectOrderedInput = <T,>({
	options: externalOptions = [],
	fetchOptions,
	value,
	onChange,
	onSearchChange,
	errored,
	disabled,
	selectionVariant = "select",
	closeOnSelect,
	placeholder = "Select options...",
	id,
	"data-testid": dataTestId,
	renderOrderedItem,
	renderOption,
	dangerouslySetClassName,
	virtualizationConfig,
	position = "bottom",
}: MultipleSelectOrderedInputProps<T>) => {
	const result = useSelectBase<T>({
		mode: "multiple",
		options: externalOptions,
		fetchOptions,
		value,
		onChange,
		onSearchChange,
		errored,
		disabled,
		selectionVariant,
		virtualizationConfig,
		id,
		closeOnSelect,
	});

	if (result.mode !== "multiple") {
		throw new Error("Expected multiple mode");
	}

	const {
		inputId,
		hasError,
		isDisabled,
		isOpen,
		searchValue,
		isLoading,
		selectedOptions,
		filteredOptions,
		options,
		triggerWidth,
		triggerRef,
		searchInputRef,
		parentRef,
		virtualizer,
		containerHeight,
		focusedIndex,
		isKeyboardNavActive,
		handleKeyDown,
		handleItemMouseEnter,
		handleItemMouseLeave,
		setSearchValue,
		handleOpenChange,
		handleItemSelect,
	} = result;

	const getItemId = (val: T): string => {
		if (typeof val === "object" && val !== null && "id" in val) {
			return String((val as { id: unknown }).id);
		}
		return typeof val === "string" ? val : JSON.stringify(val);
	};

	const getLabel = React.useCallback(
		(val: T): string => {
			const option = selectedOptions.find((opt) => {
				if (typeof val === "object" && val !== null && typeof opt.value === "object" && opt.value !== null) {
					return JSON.stringify(opt.value) === JSON.stringify(val);
				}
				return opt.value === val;
			});
			const label = option?.label;
			if (typeof label === "string") {
				return label;
			}
			return String(val);
		},
		[selectedOptions]
	);

	const renderItem = React.useCallback(
		(
			val: T,
			index: number,
			onRemove: () => void,
			dragHandlers: DragHandlers,
			isDragging: boolean
		): React.ReactNode => {
			if (renderOrderedItem) {
				return renderOrderedItem(val, index, onRemove, dragHandlers, isDragging);
			}
			return undefined;
		},
		[renderOrderedItem]
	);

	return (
		<div className={cn("flex w-full flex-col gap-1", dangerouslySetClassName)}>
			<Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
				<div className="relative w-full">
					<Popover.Trigger asChild>
						<div
							ref={triggerRef}
							data-errored={hasError}
							data-disabled={isDisabled}
							data-focused={isOpen}
							className={cn(inputContainerBaseClasses)}
						>
							<div className="flex h-full flex-1 items-center py-1 pl-3 pr-0">
								<input
									className={cn(
										"w-full border-none bg-transparent text-paragraph-3 caret-transparent outline-none placeholder:text-neutral-5 focus:outline-none",
										isDisabled ? "cursor-not-allowed" : "cursor-pointer"
									)}
									placeholder={placeholder}
									data-testid={dataTestId}
									disabled={isDisabled}
									id={inputId}
									readOnly
								/>
							</div>

							<div className="flex shrink-0 items-center p-2 pl-0">
								<div className="w-4" />
							</div>
						</div>
					</Popover.Trigger>

					<div className="pointer-events-none absolute right-2 top-1/2 flex -translate-y-1/2 items-center justify-center">
						<Icon icon={faChevronDown} size={16} color="neutral-5" />
					</div>
				</div>

				<MultipleSelectDropdown
					triggerWidth={triggerWidth}
					selectionVariant={selectionVariant}
					searchValue={searchValue}
					onSearchChange={(newSearchValue: string) => {
						setSearchValue(newSearchValue);
						onSearchChange?.(newSearchValue);
					}}
					isLoading={isLoading}
					filteredOptions={filteredOptions}
					options={options}
					value={value}
					renderOption={renderOption}
					isDisabled={isDisabled}
					onItemSelect={handleItemSelect}
					fetchOptions={fetchOptions}
					searchInputRef={searchInputRef}
					parentRef={parentRef}
					virtualizer={virtualizer}
					containerHeight={containerHeight}
					focusedIndex={focusedIndex}
					isKeyboardNavActive={isKeyboardNavActive}
					handleKeyDown={handleKeyDown}
					handleItemMouseEnter={handleItemMouseEnter}
					handleItemMouseLeave={handleItemMouseLeave}
					virtualizationConfig={virtualizationConfig}
					position={position}
				/>
			</Popover.Root>

			{selectedOptions.length > 0 && (
				<SortableOrderedList
					values={value}
					onChange={onChange}
					disabled={isDisabled}
					renderItem={renderOrderedItem ? renderItem : undefined}
					getLabel={getLabel}
					getItemId={getItemId}
					data-testid="ordered-items-list"
				/>
			)}
		</div>
	);
};

MultipleSelectOrderedInput.displayName = "MultipleSelectOrderedInput";

export { MultipleSelectOrderedInput, type MultipleSelectOrderedInputProps };
